import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { DevelopersCompanyContext } from 'common/containers/DevelopersCompanyContainer';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import withContexts from 'common/util/withContexts';

import 'css/components/developers/docs/changelog/_ChangelogCustomizationsDocs.scss';

class ChangelogCustomizationsDocs extends Component {
  static propTypes = {
    company: PropTypes.object,
  };

  render() {
    const { company } = this.props;
    const labels = company?.changelog && company?.changelog.labels;
    const companyID = company?._id ? company._id : 'YOUR_APP_ID';
    return (
      <div className="changelogCustomizationsDocs">
        <h1 className="title">Customizations</h1>
        <p className="text">
          There are a few other customizations that you might want to make when embedding your
          changelog widget.
        </p>
        <section className="step">
          <h2 className="subheading">Customize widget position and alignment</h2>
          <p className="text">
            The <span className="code">position</span> parameter determines which side of the button
            the widget appears on. It can be set to <span className="code">top</span>,{' '}
            <span className="code">bottom</span>, <span className="code">left</span>, or{' '}
            <span className="code">right</span>.
          </p>
          <p className="text">
            The <span className="code">align</span> parameter determines which side of the button
            the widget is aligned with. It can be set to <span className="code">top</span>,{' '}
            <span className="code">bottom</span>, <span className="code">left</span>, or{' '}
            <span className="code">right</span>.
          </p>
          <p className="text">
            If <span className="code">position</span> is <span className="code">top</span>/
            <span className="code">bottom</span>, then <span className="code">align</span> must be{' '}
            <span className="code">left</span>/<span className="code">right</span>, and vice-versa.
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: PositionAlignSnippet.replace('YOUR_APP_ID', companyID),
                language: 'js',
              },
            ]}
            showSelector={false}
          />
        </section>
        <section className="step">
          <h2 className="subheading">Customize the unseen badge style</h2>
          <h3 className="subSectionTitle">Customize the CSS</h3>
          <p className="text">
            When there are new entries that your user has not seen yet, we will add a red badge to
            your trigger button.
          </p>
          <p className="text">
            By default, we style the badge using absolute positioning so that it does not disrupt
            your layout.
          </p>
          <p className="text">
            If you'd like to customize this badge, you can do this in the following way:
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: CustomizeBadgeSnippet,
                name: 'CSS',
                language: 'css',
              },
            ]}
            showSelector={false}
          />
          <h3 className="subSectionTitle">Add the unseen badge on a different element</h3>
          <p className="text">
            If you would like the unseen badge to appear on a different element than the button that
            opens the changelog, you can conditionally apply styling based on the response from the
            'hasUnseenEntries' function. You can also register a callback function that gets called
            when the changelog is opened using the 'registerOnChangelogOpenCallback' function.
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: HasUnseenEntriesSnippet,
                language: 'js',
              },
            ]}
            showSelector={false}
          />
        </section>
        <section className="step">
          <h2 className="subheading">Filter the changelog</h2>
          <p className="text">
            To show entries that have a specific label, add this additional field with the desired
            label IDs.
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: FilterLabelSnippet.replace('YOUR_APP_ID', companyID),
                language: 'js',
              },
            ]}
            showSelector={false}
          />
          {labels && labels.length ? (
            <ul className="availableLabels">
              Available labels and IDs:
              {labels.map((label) => (
                <li key={label._id}>
                  <span className="labelName">{label.name}</span> (
                  <span className="labelID">{label._id}</span>)
                </li>
              ))}
            </ul>
          ) : null}
        </section>
        <section className="step">
          <h2 className="subheading">Close the changelog</h2>
          <p className="text">
            If you want to close the changelog, for example when the user navigates to another page
            in an single-page app:
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: CloseReactSnippet,
                name: 'React',
                language: 'js',
              },
              {
                code: CloseAngularSnippet,
                name: 'Angular',
                language: 'js',
              },
              {
                code: CloseVueSnippet,
                name: 'Vue.js',
                language: 'js',
              },
            ]}
          />
        </section>
        <section className="step">
          <h2 className="subheading">Turn off non-essential cookies</h2>
          <p className="text">
            If you want to turn off non-essential cookies, add this additional field.
          </p>
          <MultiLanguageCodeBlock
            blocks={[
              {
                code: NonEssentialCookiesSnippet.replace('YOUR_APP_ID', companyID),
                language: 'js',
              },
            ]}
            showSelector={false}
          />
        </section>
      </div>
    );
  }
}

const CloseAngularSnippet = `ngOnDestroy() {\n  Canny('closeChangelog');\n}`;
const CloseReactSnippet = `componentWillUnmount() {\n  Canny('closeChangelog');\n}`;
const CloseVueSnippet = `beforeDestroy() {\n  Canny('closeChangelog');\n}`;
const CustomizeBadgeSnippet = `/* These are the default badge styles */\n.Canny_BadgeContainer .Canny_Badge {\n  position: absolute;\n  top: -1px;\n  right: -1px;\n  border-radius: 10px;\n  background-color: red;\n  padding: 5px;\n  border: 1px solid white;\n}`;
const FilterLabelSnippet = `Canny('initChangelog', {\n  appID: 'YOUR_APP_ID',\n  position: 'bottom',\n  align: 'left',\n  // only entries that have one of these labels will be shown\n  labelIDs: ['5f1234567890abcdef123111', '5f1234567890abcdef123222'],\n  theme: 'light', // options: light [default], dark, auto\n});`;
const PositionAlignSnippet = `Canny('initChangelog', {\n  appID: 'YOUR_APP_ID',\n  position: 'bottom', // widget is positioned below your button\n  align: 'left', // widget's left side is aligned with your button's left side\n  theme: 'light', // options: light [default], dark, auto\n});`;
const NonEssentialCookiesSnippet = `Canny('initChangelog', {\n  appID: 'YOUR_APP_ID',\n  position: 'bottom',\n  align: 'left',\n  // only entries that have one of these labels will be shown\n  labelIDs: ['5f1234567890abcdef123111', '5f1234567890abcdef123222'],\n  theme: 'light', // options: light [default], dark, auto\n  omitNonEssentialCookies: true,\n});`;
const HasUnseenEntriesSnippet = `const hasUnseenEntries = Canny('hasUnseenEntries');\nif (hasUnseenEntries) {\n  // apply styles\n  Canny('registerOnChangelogOpenCallback', () => {\n    // remove styles\n  });\n};`;

export default compose(
  withContexts({ company: DevelopersCompanyContext }),
  hot,
)(ChangelogCustomizationsDocs);
